import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Table as TanTable } from '@tanstack/react-table';

export const useFixedHeaderScrollSync = <D extends object = object>(table: TanTable<D>) => {
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyFirstColumnRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  const [hasBodyHorizontalScroll, setHasBodyHorizontalScroll] = useState(false);

  const updateHorizontalScrollState = useCallback(() => {
    if (!bodyScrollRef.current) return;

    const { scrollWidth, clientWidth } = bodyScrollRef.current;
    setHasBodyHorizontalScroll(scrollWidth > clientWidth);
  }, []);

  const handleScroll = useCallback(() => {
    if (bodyScrollRef.current && headerScrollRef.current)
      headerScrollRef.current.scrollLeft = bodyScrollRef.current.scrollLeft;

    if (bodyScrollRef.current && bodyFirstColumnRef.current)
      bodyFirstColumnRef.current.scrollTop = bodyScrollRef.current.scrollTop;

    updateHorizontalScrollState();
  }, [updateHorizontalScrollState]);

  useLayoutEffect(() => {
    updateHorizontalScrollState();
    if (!bodyScrollRef.current) return;

    const resizeObserver = new ResizeObserver(updateHorizontalScrollState);
    resizeObserver.observe(bodyScrollRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [table, updateHorizontalScrollState]);

  return {
    headerScrollRef,
    bodyFirstColumnRef,
    bodyScrollRef,
    hasBodyHorizontalScroll,
    handleScroll,
  };
};
